import request, { getRequestOrgScope, RequestOrganization } from 'src/api/utils';
import * as ROUTES from 'src/api/routes';
import { setMfaToken } from 'src/utils/mfaToken';
import { UserStoreObject } from 'src/stores/UserStore';
import { AuthStoreObject } from 'src/stores/AuthStore';

export type MfaMethodType = 'email' | 'sms' | 'totp';

export interface MfaPolicy {
    enabled: boolean;
    required: boolean;
    enforced?: boolean;
    gracePeriodEndDttm?: string | null;
    gracePeriodEndDate?: string | null;
    allowedMethods: MfaMethodType[];
    inGracePeriod: boolean;
    trustedDeviceEnabled: boolean;
    trustedDeviceTtlHours: number;
}

export interface MfaMethod {
    usrMfaMethodId: number;
    mfaMethodTyp: string;
    displayNm?: string | null;
    verifiedInd: boolean;
    actvInd?: boolean;
}

export interface MfaAccountEmail {
    emailAddr: string;
    displayNm: string;
    verifiedInd: boolean;
    mfaEnabledInd: boolean;
    verifiedDttm?: string | null;
}

export interface MfaStatus {
    policy: MfaPolicy;
    user: {
        exempt: boolean;
        accountEmail: MfaAccountEmail;
        methods: MfaMethod[];
    };
    enrolled?: boolean;
    satisfied: boolean;
    setupRequired: boolean;
    verificationRequired?: boolean;
}

export interface MfaChallengeData {
    methodType: 'email' | 'sms';
    phoneNumber?: string;
    methodId?: number;
}

export interface MfaChallenge {
    challengeId: number;
    destination: string;
    expiresAt: string;
}

export interface MfaVerifyData {
    challengeId?: number;
    methodType: MfaMethodType;
    code: string;
    phoneNumber?: string;
    totpSecret?: string;
    rememberDevice?: boolean;
    deviceType?: 'web' | 'mobile';
    deviceLabel?: string;
}

export interface MfaVerifyResult {
    mfaToken: string;
    method?: MfaMethod | object;
    trustedDevice?: MfaTrustedDevice;
    trustedDeviceToken?: string;
}

export interface TotpSetupResult {
    secret: string;
    otpauthUrl: string;
}

export interface OrgMfaSettings {
    enabled: boolean;
    gracePeriodEndDttm?: string | null;
    gracePeriodEndDate?: string | null;
    allowedMethods: string[];
    trustedDeviceEnabled?: boolean;
    trustedDeviceTtlHours?: number;
}

export interface MfaTrustedDevice {
    usrMfaTrustedDeviceId: number;
    deviceTyp: 'web' | 'mobile';
    deviceLabel?: string | null;
    mfaMethodTyp: MfaMethodType;
    usrMfaMethodId?: number | null;
    expiresDttm: string;
    lastUsedDttm?: string | null;
    insDttm?: string | null;
}

function unwrapResponse<T>(response: any): T {
    return (response.data?.result ?? response.data) as T;
}

export async function getMfaStatus(organization?: RequestOrganization): Promise<MfaStatus> {
    const response = await request({ ...ROUTES.GET_MFA_STATUS, organization });
    return unwrapResponse<MfaStatus>(response);
}

export async function createMfaChallenge(
    data: MfaChallengeData,
    organization?: RequestOrganization,
): Promise<MfaChallenge> {
    const response = await request({ ...ROUTES.CREATE_MFA_CHALLENGE, organization, data });
    return unwrapResponse<MfaChallenge>(response);
}

export async function verifyMfaChallenge(
    data: MfaVerifyData,
    organization?: RequestOrganization,
): Promise<MfaVerifyResult> {
    const orgScope = getRequestOrgScope(organization);
    const accessToken = AuthStoreObject.accessToken;
    const username = UserStoreObject.usrNm;
    const response = await request({ ...ROUTES.VERIFY_MFA_CHALLENGE, organization, data, withCredentials: true });
    const result = unwrapResponse<MfaVerifyResult>(response);
    if (AuthStoreObject.accessToken !== accessToken || UserStoreObject.usrNm !== username) {
        throw new Error('Your session changed. Please sign in again.');
    }
    setMfaToken(result.mfaToken, orgScope);
    return result;
}

export async function createTrustedDeviceSession(organization?: RequestOrganization): Promise<MfaVerifyResult> {
    const orgScope = getRequestOrgScope(organization);
    const accessToken = AuthStoreObject.accessToken;
    const username = UserStoreObject.usrNm;
    const response = await request({
        ...ROUTES.CREATE_MFA_TRUSTED_DEVICE_SESSION,
        organization,
        data: { deviceType: 'web' },
        withCredentials: true,
    });
    const result = unwrapResponse<MfaVerifyResult>(response);
    if (AuthStoreObject.accessToken !== accessToken || UserStoreObject.usrNm !== username) {
        throw new Error('Your session changed. Please sign in again.');
    }
    setMfaToken(result.mfaToken, orgScope);
    return result;
}

export async function setupTotp(organization?: RequestOrganization): Promise<TotpSetupResult> {
    const response = await request({ ...ROUTES.SETUP_MFA_TOTP, organization });
    return unwrapResponse<TotpSetupResult>(response);
}

export async function deleteMfaMethod(methodId: number) {
    const response = await request({ ...ROUTES.DELETE_MFA_METHOD, pathParams: { methodId } });
    return unwrapResponse(response);
}

export async function sendEmailVerificationChallenge(): Promise<MfaChallenge> {
    const response = await request({ ...ROUTES.SEND_MFA_EMAIL_VERIFICATION });
    return unwrapResponse<MfaChallenge>(response);
}

export async function verifyEmail(data: { challengeId: number; code: string }): Promise<MfaAccountEmail> {
    const response = await request({ ...ROUTES.VERIFY_MFA_EMAIL, data });
    return unwrapResponse<MfaAccountEmail>(response);
}

export async function updateEmailMfaEnabled(
    enabled: boolean,
    organization?: RequestOrganization,
): Promise<MfaAccountEmail> {
    const response = await request({ ...ROUTES.UPDATE_EMAIL_MFA_ENABLED, organization, data: { enabled } });
    return unwrapResponse<MfaAccountEmail>(response);
}

export async function getTrustedDevices(): Promise<MfaTrustedDevice[]> {
    const response = await request({ ...ROUTES.GET_MFA_TRUSTED_DEVICES, withCredentials: true });
    return unwrapResponse<MfaTrustedDevice[]>(response);
}

export async function deleteTrustedDevice(trustedDeviceId: number) {
    const response = await request({
        ...ROUTES.DELETE_MFA_TRUSTED_DEVICE,
        pathParams: { trustedDeviceId },
        withCredentials: true,
    });
    return unwrapResponse(response);
}

export async function getOrgMfaSettings(): Promise<OrgMfaSettings> {
    const response = await request({ ...ROUTES.GET_ORG_MFA_SETTINGS });
    return unwrapResponse<OrgMfaSettings>(response);
}

export async function updateOrgMfaSettings(data: OrgMfaSettings): Promise<OrgMfaSettings> {
    const response = await request({ ...ROUTES.UPDATE_ORG_MFA_SETTINGS, data });
    return unwrapResponse<OrgMfaSettings>(response);
}
